/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { ChevronRightIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link } from "react-router"

import { cn } from "@/utils/cn"

import { getApplicationDetailVM } from "../../app-context"
import type { PageApplicationItem } from "../../features/application/application-types"
import { deviceName } from "../../features/application/device-name"
import { AppRoutes } from "../../routes/routes"
import { ClipboardValue } from "../components/clipboard-value"
import { RiskScore } from "../components/risk-score"
import { TimestampWithIcon } from "../components/timestamp-with-icon"
import { buttonVariants } from "../primitives/button"
import { Card, CardAction, CardContent, CardFooter, CardHeader, CardTitle } from "../primitives/card"

import styles from "./application-list-item.module.css"

interface Props {
    item: PageApplicationItem
}

export const ApplicationListItem = observer(({ item }: Props) => {
    // `id` is a primitive `long` on the Java record, so the API always sends it.
    // It arrives optional here only because that record carries no
    // `@Schema(requiredMode = REQUIRED)`, the way `OrganizationDetailResponse`
    // carries `@NotNull`. Annotating the device records would retire this cast.
    const id = item.id as number

    const device = deviceName(item)

    return (
        <Card className={styles.card}>
            <CardHeader className={styles.header}>
                <CardTitle className="pt-1">
                    <h2 className="m-0 text-xl font-semibold">
                        <span className="pe-2 font-normal">{device.brand}</span>
                        <span className="wrap-anywhere">{device.model}</span>
                    </h2>
                </CardTitle>
                <CardAction>
                    <RiskScore score={item.riskScore} />
                </CardAction>
            </CardHeader>
            <CardContent className={styles.body}>
                <dl className={styles.table}>
                    <dt className={styles.key}>Client User ID:</dt>
                    <dd className={clsx("font-mono", styles.value)}>
                        <ClipboardValue value={item.clientUserId} />
                    </dd>

                    <dt className={styles.key}>Client Device ID:</dt>
                    <dd className={clsx("font-mono", styles.value)}>
                        <ClipboardValue value={item.clientDeviceId} />
                    </dd>

                    <dt className={styles.key}>Internal Device ID:</dt>
                    <dd className={clsx("font-mono", styles.value)}>
                        <ClipboardValue value={item.avUserId} />
                    </dd>
                </dl>
            </CardContent>
            <CardFooter className={styles.footer}>
                <dl className={clsx("gap-y-1", styles.table)}>
                    <dt className={styles.key}>First Seen:</dt>
                    <dd className={styles.value}>
                        <TimestampWithIcon kind="created" timestamp={item.timestampCreated} />
                    </dd>

                    {item.timestampUpdated && (
                        <>
                            <dt className={styles.key}>Updated:</dt>
                            <dd className={styles.value}>
                                <TimestampWithIcon kind="updated" timestamp={item.timestampUpdated} />
                            </dd>
                        </>
                    )}

                    <dt className={styles.key}>Last Seen:</dt>
                    <dd className={styles.value}>
                        <TimestampWithIcon kind="lastSeen" timestamp={item.timestampLastSeen} />
                    </dd>
                </dl>
                <Link
                    to={AppRoutes.ApplicationDetail.replace(":id", `${id}`)}
                    className={cn(buttonVariants({ variant: "outline", size: "sm" }), styles.link)}
                    // Give each link a unique accessible name instead of "View Detail" for every card.
                    aria-label={device.label ? `View detail of ${device.label}` : undefined}
                    // Prefill the detail view with data already available in the list.
                    onClick={() => getApplicationDetailVM(id).setPrefetchedAppDetail(item)}
                >
                    View Detail
                    <ChevronRightIcon data-icon="inline-end" />
                </Link>
            </CardFooter>
        </Card>
    )
})
